import React, { useContext } from 'react'
import { PortableText, PortableTextComponents } from '@portabletext/react'
import Link from 'next/link'

import {
  Button,
  Img,
  PrimaryLayout,
  SEO,
  TextHighlight,
} from '../../components'
import { Context } from '../../context'
import { buildStaticProps } from '../../ssg/direct'
import { getSEOFromCategory } from '../../utils'
import { RateQuote, Sanity } from '../../../types'

const CUSTOM_RQDIRECT_COMPONENTS: PortableTextComponents = {
  block: {
    h3: (props: any) => (
      <h3 className="text-blue-navy c-direct-headline-padding text-4-1/2xl mb-6 font-normal leading-9">
        {props.children}
      </h3>
    ),
    normal: (props: any) => (
      <p className="font-roboto mb-4 text-xl leading-9 empty:hidden">
        {props.children}
      </p>
    ),
    small: (props: any) => (
      <p className="text-gray-primary my-2 text-sm leading-6 empty:hidden">
        {props.children}
      </p>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="text-gray-primary font-roboto ml-8 mb-6 list-disc text-xl leading-9">
        {children}
      </ul>
    ),
  },
  listItem: ({ children }) => <li className="c-direct-marker">{children}</li>,
  types: {
    button: (props: any) => (
      <Link href={props.value.buttonUrl}>
        <Button
          classes="max-w-fit xl:mt-12 mx-auto md:mx-0 hover:bg-blue-primary hover:border-white"
          icon={<img src="/static/icons/mail.svg" className="mr-3" />}
        >
          {props.value.buttonText}
        </Button>
      </Link>
    ),
  },
}

export default function RateQuoteDirect(props: Sanity.Direct) {
  const {
    bottomCTAContent,
    content,
    contentHeadline,
    description,
    rqdirectDisclaimer,
    title,
  } = props

  const ctx = useContext<RateQuote.AppState>(Context)
  const year = new Date().getFullYear()

  return (
    <PrimaryLayout isDirect>
      <SEO
        title={props.seoTitle || 'RateQuote Direct'}
        description={
          props.seoDescription ||
          `RateQuote Direct - ${getSEOFromCategory(ctx.user.category)}`
        }
      />
      <div className="c-bg-blue-radial left-0" />
      <div className="c-bg-direct-rectangle left-0" />

      <div className="border-b">
        <div className="my-20 grid justify-items-center px-4 text-center md:mt-12 md:justify-items-end md:px-16">
          <div className="text-5xl tracking-tighter lg:text-7xl">
            <TextHighlight emphasisIndex={[1]} title={title} />
          </div>
          <p className="text-gray-primary font-roboto xxl:w-1/2 py-6 text-xl md:w-2/3 md:text-right">
            {description}
          </p>
          <Link href="/partner/">
            <Button classes="px-8 max-w-fit">Partner With Us</Button>
          </Link>
        </div>
        <div className="c-bg-blur text-gray-primary c-direct-content-shadow border-3 rounded-xl border-white p-5 sm:p-10">
          <p className="mb-6 border-b pb-5 text-center text-xl uppercase tracking-wider md:pb-10 md:text-3xl">
            {contentHeadline}
          </p>
          <PortableText
            components={{ ...CUSTOM_RQDIRECT_COMPONENTS }}
            value={content}
          />
        </div>
        <div className="bg-blue-navy relative my-20 flex overflow-hidden px-10 pb-16 pt-10 sm:rounded-xl">
          <div className="md:justify-left w-full justify-center pt-4 text-center text-white md:pl-6 md:text-left">
            <Img
              alt="RQ logo"
              classes="absolute w-80 sm:w-425px opacity-25 sm:opacity-40 -right-10 -rotate-3"
              src="/static/logos/rq-logo-wire.svg"
            />
            <PortableText
              components={{ ...CUSTOM_RQDIRECT_COMPONENTS }}
              value={bottomCTAContent}
            />
          </div>
        </div>
      </div>
      <div className="mx-5 md:mx-0 xl:mx-6">
        <p className="text-gray-primary mt-7 mb-4 text-sm">
          &copy;{year} RateQuote Direct, LLC
        </p>
        <div className="mb-7">
          <PortableText
            components={{ ...CUSTOM_RQDIRECT_COMPONENTS }}
            value={rqdirectDisclaimer}
          />
        </div>
      </div>
    </PrimaryLayout>
  )
}

export async function getStaticProps(): Promise<{
  props: Sanity.Direct
}> {
  return await buildStaticProps()
}
